import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProfileViewer, useNavGuard } from "@/components/Layout";
import { ReviewsModal } from "@/components/ReviewsModal";
import { Compass } from "@/components/Compass";
import { BraveBadge } from "@/components/BraveBadge";
import { GeniusBadge } from "@/components/GeniusBadge";
import { BagButton } from "@/components/Bag";
import { CompletedUndertakings } from "@/components/CompletedUndertakings";
import { UndertakerRank } from "@/components/UndertakerRank";
import { MemberPosts } from "@/components/MemberPosts";
import { XpProofModal } from "@/components/XpProofModal";
import { formatApiError, limitWords } from "@/lib/format";
import { COUNTRIES } from "@/lib/countries";
import { Check, Trophy, Orbit, Clock, Sword, Flame, Heart } from "lucide-react";

const COUNTRY_LC = COUNTRIES.map((c) => c.toLowerCase());
import { toast } from "sonner";

const XP_SLOTS = 10;
const TEAMS = ["attack", "defend", "grow", "build", "maintain", "teach", "explore", "play", "master"];

function padXp(list) {
  const arr = Array.isArray(list) ? list.slice(0, XP_SLOTS) : [];
  while (arr.length < XP_SLOTS) arr.push({ title: "", description: "" });
  return arr.map((e) => ({ title: e?.title || "", description: e?.description || "", verified: !!e?.verified }));
}

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { viewProfile } = useProfileViewer();
  const isGuest = user?.is_guest;
  const [name, setName] = useState(user?.name || "");
  const [location, setLocation] = useState(user?.location || "");
  const [team, setTeam] = useState(user?.team || "");
  const [compass, setCompass] = useState(user?.compass || "");
  const [teamOpen, setTeamOpen] = useState(false);
  const [rankedOpen, setRankedOpen] = useState(false);
  const [ranked, setRanked] = useState(null);
  const [gods, setGods] = useState({});
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [xpReqs, setXpReqs] = useState({});
  const [proofIndex, setProofIndex] = useState(null);
  const [xp, setXp] = useState(padXp(user?.natural_xp));
  const [editIndex, setEditIndex] = useState(null);
  const [confirmIndex, setConfirmIndex] = useState(null);
  const [sureIndex, setSureIndex] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [lastSaved, setLastSaved] = useState("");
  const { registerGuard } = useNavGuard();

  const loadXpReqs = () => {
    if (!user || user.is_god || user.is_guest) return;
    api.get("/xp-requests/mine").then(({ data }) => setXpReqs(Object.fromEntries(data.map((r) => [r.index, r])))).catch(() => {});
  };
  useEffect(loadXpReqs, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openProof = (i) => {
    const saved = padXp(user?.natural_xp)[i];
    if (!xp[i].title) { toast("write this entry first"); return; }
    if (saved.title !== xp[i].title || saved.description !== xp[i].description) { toast("save this entry first"); return; }
    setProofIndex(i);
  };

  const handleLocationChange = (val) => {
    const words = val.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) { setLocation(val); return; }
    const lc = val.trim().toLowerCase();
    const isCountry = COUNTRY_LC.some((c) => c === lc || c.startsWith(lc));
    if (isCountry) setLocation(val);
  };

  const isDirty = () =>
    !isGuest && (
      location !== (user?.location || "") ||
      team !== (user?.team || "") ||
      compass !== (user?.compass || "") ||
      JSON.stringify(xp) !== JSON.stringify(padXp(user?.natural_xp))
    );

  useEffect(() => {
    registerGuard({ isDirty, save });
    return () => registerGuard(null);
  });

  useEffect(() => {
    const handler = (e) => { if (isDirty()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  });

  useEffect(() => { setXp(padXp(user?.natural_xp)); }, [user?.natural_xp]);
  useEffect(() => { setTeam(user?.team || ""); }, [user?.team]);
  useEffect(() => { setCompass(user?.compass || ""); }, [user?.compass]);
  useEffect(() => { setLastSaved(user?.profile_updated_at || ""); }, [user?.profile_updated_at]);

  const selectTeam = (t) => {
    if (isGuest) return guestBlock();
    setTeam(t);
    setTeamOpen(false);
  };

  const openRanked = async () => {
    setRankedOpen(true);
    setRanked(null);
    api.get("/gods").then(({ data }) => setGods(data)).catch(() => setGods({}));
    try {
      const { data } = await api.get("/ranked");
      setRanked(data);
    } catch (err) {
      setRanked([]);
    }
  };

  const requestEdit = (i) => {
    if (isGuest) return guestBlock();
    if (xp[i]?.verified) { setConfirmIndex(i); return; }
    openEditor(i);
  };

  const openEditor = (i) => {
    if (isGuest) return guestBlock();
    setEditIndex(i);
    setEditTitle(xp[i].title);
    setEditDesc(xp[i].description);
  };

  const saveEntry = () => {
    const next = xp.map((e, i) => (i === editIndex ? { title: editTitle.trim(), description: editDesc.trim() } : e));
    setXp(next);
    setEditIndex(null);
  };

  useEffect(() => {
    if (user?.id) api.get(`/users/${user.id}`).then(({ data }) => setStats(data));
  }, [user?.id]);

  const guestBlock = () => {
    toast("Sign in");
  };

  const save = async () => {
    if (isGuest) return guestBlock();
    setBusy(true);
    try {
      await api.put("/profile", { location, team, compass, natural_xp: xp });
      loadXpReqs();
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    background: "transparent",
    borderBottom: "1px solid var(--border)",
    color: "var(--text-primary)",
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-head font-light text-4xl tracking-tighter mb-8" style={{ color: "var(--text-primary)" }}>
        Profile
      </h1>

      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="font-head text-3xl break-words min-w-0" style={{ color: "var(--text-primary)" }} data-testid="profile-username-display">
            {user?.name || "Unnamed"}
          </h2>
          {user?.is_genius && <GeniusBadge />}
          {user?.is_brave && <BraveBadge />}
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <span className="font-head text-3xl" style={{ color: "var(--text-primary)" }} data-testid="profile-avg-rating">
            {user?.is_god ? <span style={{ fontSize: "1.5em", lineHeight: 1, verticalAlign: "-0.12em" }}>∞</span> : (stats && stats.average_rating != null ? `${stats.average_rating.toFixed(2)}/10` : "—")}
          </span>
          <button
            onClick={openRanked}
            data-testid="ranked-btn"
            title="Ranked"
            className="btn flex items-center justify-center w-[46px] h-[46px]"
          >
            <Trophy size={20} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-8 mb-14">
        <div>
          <p className="text-base" style={{ color: "var(--text-secondary)" }} data-testid="profile-prompt-count">
            {user?.is_god ? <span style={{ fontSize: "1.6em", lineHeight: 1, verticalAlign: "-0.15em" }}>∞</span> : (stats ? stats.prompt_count : 0)} asks
          </p>
          <p className="text-base mb-4" style={{ color: "var(--text-secondary)" }} data-testid="profile-response-count">
            {user?.is_god ? <span style={{ fontSize: "1.6em", lineHeight: 1, verticalAlign: "-0.15em" }}>∞</span> : (stats ? stats.response_count : 0)} answers
          </p>
          <button
            onClick={() => setReviewsOpen(true)}
            data-testid="profile-reviews-btn"
            className="btn text-xs tracking-widest px-4 py-2"
          >
            REVIEWS · {user?.is_god ? <span style={{ fontSize: "1.6em", lineHeight: 1, verticalAlign: "-0.15em" }}>∞</span> : (stats ? stats.review_count || 0 : 0)}
          </button>
          <div className="mt-3"><BagButton amount={user?.provisions} isGod={user?.is_god} testId="profile-bag-btn" /></div>
        </div>
        <Compass value={compass} onChange={isGuest ? undefined : (v) => setCompass(v || "")} />
        <div className="min-w-0 flex-1 self-stretch flex flex-col justify-between gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Country</label>
            <input data-testid="profile-location-input" value={location} onChange={(e) => handleLocationChange(e.target.value)} readOnly={isGuest} disabled={isGuest} className="w-full py-1 mt-1 text-base disabled:opacity-60 disabled:cursor-not-allowed" style={inputStyle} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>walk</label>
            <div className="flex items-stretch gap-2 mt-1">
              <input
                data-testid="profile-team-input"
                value={team}
                readOnly
                className="flex-1 min-w-0 py-1 text-base"
                style={inputStyle}
              />
              <button
                onClick={() => (isGuest ? guestBlock() : setTeamOpen(true))}
                data-testid="profile-team-select-btn"
                className="btn shrink-0 flex items-center justify-center w-[46px] text-sm"
              >
                ...
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-10">
        {user?.is_god ? (
          <MemberPosts userId={user.id} canPost />
        ) : (
        <div>
          <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Natural XP</label>
          <div className="flex flex-col gap-3 mt-3" data-testid="natural-xp-grid">
            {xp.map((e, i) => (
              <div key={i} className="contents">
              <div className="flex items-stretch gap-3">
                <button
                  onClick={() => requestEdit(i)}
                  data-testid={`natural-xp-slot-${i}`}
                  className="btn flex-1 min-w-0 text-left px-4 py-3 text-sm truncate"
                  style={!e.title ? { color: "var(--text-muted)" } : undefined}
                >
                  {e.title || `entry ${i + 1}`}
                </button>
                <button
                  type="button"
                  data-testid={`natural-xp-verify-${i}`}
                  onClick={() => openProof(i)}
                  disabled={user?.is_god || isGuest}
                  title={e.verified ? "verified" : "submit proof for verification"}
                  className="shrink-0 flex items-center justify-center w-[46px] transition-colors duration-200 hover:bg-[var(--bg-secondary)]"
                  style={{ border: `1px ${xpReqs[i]?.status === "pending" && !e.verified ? "dashed var(--accent)" : "solid var(--border)"}`, color: (e.verified || user?.is_god) ? "var(--accent)" : "#5A5A5A" }}
                >
                  <Check size={18} strokeWidth={2} />
                </button>
              </div>
              {!e.verified && xpReqs[i]?.status === "pending" && <p className="text-[10px] tracking-widest -mt-2" style={{ color: "var(--accent)" }} data-testid={`natural-xp-pending-${i}`}>awaiting verification</p>}
              {!e.verified && xpReqs[i]?.status === "declined" && <p className="text-[10px] tracking-widest -mt-2" style={{ color: "var(--accent)" }} data-testid={`natural-xp-declined-${i}`}>declined: {xpReqs[i].reason}</p>}
              </div>
            ))}
          </div>
        </div>
        )}

        <div className="flex items-center gap-4">
          <button
            onClick={save}
            disabled={busy}
            data-testid="profile-save-btn"
            className="btn self-start px-8 py-3 text-sm tracking-widest"
          >
            {busy ? "…" : "SAVE"}
          </button>
          {lastSaved && (
            <span className="text-xs tracking-widest" style={{ color: "var(--text-muted)" }} data-testid="profile-last-saved">
              last saved {new Date(lastSaved).toLocaleString()}
            </span>
          )}
        </div>
        {user?.id && !user.is_god && <CompletedUndertakings userId={user.id} />}
      </div>

      {proofIndex !== null && (
        <XpProofModal index={proofIndex} entry={xp[proofIndex]} request={xpReqs[proofIndex]} onClose={() => { setProofIndex(null); loadXpReqs(); }} onChanged={loadXpReqs} />
      )}

      {confirmIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setConfirmIndex(null)}
          data-testid="xp-verified-warning-overlay"
        >
          <div
            className="w-full max-w-md p-8"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="xp-verified-warning-modal"
          >
            <p className="text-base mb-8" style={{ color: "var(--text-secondary)" }} data-testid="xp-verified-warning-text">
              this entry has been verified, any changes will revoke the verification
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmIndex(null)} data-testid="xp-verified-back-btn" className="btn flex-1 py-3 text-sm tracking-widest">BACK</button>
              <button onClick={() => { const i = confirmIndex; setConfirmIndex(null); setSureIndex(i); }} data-testid="xp-verified-edit-btn" className="btn flex-1 py-3 text-sm tracking-widest">EDIT</button>
            </div>
          </div>
        </div>
      )}

      {sureIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setSureIndex(null)}
          data-testid="xp-sure-overlay"
        >
          <div
            className="w-full max-w-sm p-8"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="xp-sure-modal"
          >
            <p className="text-base mb-8" style={{ color: "var(--text-secondary)" }} data-testid="xp-sure-text">are you sure?</p>
            <div className="flex gap-3">
              <button onClick={() => setSureIndex(null)} data-testid="xp-sure-no-btn" className="btn flex-1 py-3 text-sm tracking-widest">NO</button>
              <button onClick={() => { const i = sureIndex; setSureIndex(null); openEditor(i); }} data-testid="xp-sure-yes-btn" className="btn flex-1 py-3 text-sm tracking-widest">YES</button>
            </div>
          </div>
        </div>
      )}


      {editIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setEditIndex(null)}
          data-testid="xp-editor-overlay"
        >
          <div
            className="w-full max-w-md p-8"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(ev) => ev.stopPropagation()}
            data-testid="xp-editor-modal"
          >
            <div className="flex justify-between items-start mb-6">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Natural XP</span>
              <button onClick={() => setEditIndex(null)} data-testid="xp-editor-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>
                [ close ]
              </button>
            </div>

            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Title</label>
            <input
              data-testid="xp-title-input"
              value={editTitle}
              onChange={(ev) => setEditTitle(limitWords(ev.target.value, editTitle, 7))}
              className="w-full py-3 mt-2 text-lg"
              style={inputStyle}
            />

            <label className="text-xs uppercase tracking-widest mt-6 block" style={{ color: "var(--text-muted)" }}>Description</label>
            <textarea
              data-testid="xp-desc-input"
              value={editDesc}
              onChange={(ev) => setEditDesc(limitWords(ev.target.value, editDesc, 1000))}
              rows={5}
              className="w-full mt-2 p-3 text-base resize-none"
              style={{ background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />

            <button onClick={saveEntry} data-testid="xp-save-btn" className="btn w-full py-3 mt-6 text-sm tracking-widest">
              DONE
            </button>
          </div>
        </div>
      )}

      {teamOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setTeamOpen(false)}
          data-testid="team-modal-overlay"
        >
          <div
            className="w-full max-w-md p-8"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(ev) => ev.stopPropagation()}
            data-testid="team-modal"
          >
            <div className="flex justify-between items-start mb-6">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>walk: 9 lives</span>
              <button onClick={() => setTeamOpen(false)} data-testid="team-modal-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>
                [ close ]
              </button>
            </div>
            <div className="flex flex-col gap-3">
              {TEAMS.map((t) => (
                <button
                  key={t}
                  onClick={() => selectTeam(t)}
                  data-testid={`team-option-${t}`}
                  className="btn text-left px-4 py-3 text-sm tracking-widest"
                  style={team === t ? { background: "var(--accent)", color: "var(--bg-primary)" } : undefined}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {rankedOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setRankedOpen(false)}
          data-testid="ranked-modal-overlay"
        >
          <div
            className="w-full max-w-lg p-8 max-h-[85vh] flex flex-col overflow-y-auto"
            style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
            onClick={(ev) => ev.stopPropagation()}
            data-testid="ranked-modal"
          >
            <p className="text-sm uppercase tracking-widest mb-3" style={{ color: "var(--text-primary)" }} data-testid="ranked-members-label">the 5 members</p>
            <div className="flex items-center justify-between gap-3 mb-6" data-testid="ranked-catalysts">
              {[
                { k: "space", label: "space", Icon: Orbit },
                { k: "time", label: "time", Icon: Clock },
                { k: "matter", label: "matter", Icon: Sword },
                { k: "energy", label: "energy", Icon: Flame },
                { k: "love", label: "love", Icon: Heart },
              ].map(({ k, label, Icon }) => {
                const acct = gods[k];
                return (
                  <button
                    key={k}
                    data-testid={`ranked-catalyst-${k}`}
                    title={acct ? acct.name : label}
                    disabled={!acct}
                    onClick={acct ? () => { setRankedOpen(false); viewProfile(acct.id, { noReview: true }); } : undefined}
                    className={`btn flex-1 aspect-square flex flex-col items-center justify-center gap-1.5 ${acct ? "" : "cursor-default"}`}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                    <span className="text-[10px] tracking-widest uppercase">{label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between items-start mb-6">
              <span className="text-sm uppercase tracking-widest" style={{ color: "var(--text-primary)" }} data-testid="ranked-title">
                top 10 thinkers:
                <span className="block mt-1" data-testid="ranked-formula">x=(avg/10)(asks+answers)</span>
              </span>
              <button onClick={() => setRankedOpen(false)} data-testid="ranked-close" className="text-sm opacity-60 hover:opacity-100 shrink-0 ml-4" style={{ color: "var(--text-primary)" }}>
                [ close ]
              </button>
            </div>
            <div className="flex flex-col gap-2" data-testid="ranked-list">
              {ranked === null ? (
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
              ) : ranked.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="ranked-empty">No profiles yet.</p>
              ) : (
                ranked.map((u, i) => (
                  <button
                    key={u.id}
                    onClick={() => { setRankedOpen(false); viewProfile(u.id, { noReview: true }); }}
                    data-testid={`ranked-row-${i}`}
                    className="flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors"
                    style={{ border: "1px solid var(--border)" }}
                  >
                    <span className="flex items-center gap-4 min-w-0">
                      <span className="text-sm shrink-0" style={{ color: "var(--text-muted)", width: "2ch" }}>{i + 1}</span>
                      <span className="text-sm truncate" style={{ color: "var(--text-primary)" }}>{u.name}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
            <UndertakerRank onPick={(id) => { setRankedOpen(false); viewProfile(id, { noReview: true }); }} />
          </div>
        </div>
      )}

      {reviewsOpen && <ReviewsModal userId={user.id} onClose={() => setReviewsOpen(false)} />}

    </div>
  );
}
